import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, CircularProgress, Alert, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Avatar, TextField, InputAdornment, useTheme, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RequestService from '../services/request.service';
import dayjs from 'dayjs';
import { usePreferences } from '../contexts/PreferencesContext';

const RequestManagement = ({ onRequestAction }) => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCards, setExpandedCards] = useState({});

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        if (nameParts.length > 1) {
            return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        } else if (nameParts[0] && nameParts[0].length > 1) {
            return nameParts[0].substring(0, 2).toUpperCase();
        }
        return (name[0] || '').toUpperCase();
    };

    const fetchRequests = () => {
        setLoading(true);
        setError('');
        RequestService.getPendingRequests()
            .then(response => {
                setRequests(response.data);
            })
            .catch(error => {
                const errorMessage = (error.response?.data?.message) || error.message || "An unknown error occurred.";
                setError(errorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleOpenDialog = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRequest(null);
        setActionType('');
    };

    const handleConfirmAction = () => {
        if (!selectedRequest) return;
        setSuccess('');
        setError('');

        const actionPromise = actionType === 'approve'
            ? RequestService.approveRequest(selectedRequest.id)
            : RequestService.rejectRequest(selectedRequest.id);

        actionPromise
            .then(response => {
                setSuccess(response.data.message || `Request has been successfully ${actionType}d.`);
                fetchRequests();
                if (onRequestAction) {
                    onRequestAction();
                }
            })
            .catch(error => {
                const errorMessage = (error.response?.data?.message) || `Failed to ${actionType} request.`;
                setError(errorMessage);
            })
            .finally(() => {
                handleCloseDialog();
            });
    };

    const renderRequestDetails = (request) => {
        try {
            const payload = typeof request.payload === 'string' ? JSON.parse(request.payload) : request.payload;
            const original = request.original;

            if (request.type === 'attendance_shift_update') {
                const { side, action, requestedTime } = payload || {};
                const sideLabel = side === 'start' ? 'Príchod' : 'Odchod';
                const actionLabel = action === 'delete' ? 'Zmazanie' : 'Zmena';

                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            {actionLabel} – {sideLabel}
                        </Typography>

                        {original && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Pôvodný čas:
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                                    {original.start_time
                                        ? dayjs(original.start_time).format('DD.MM.YYYY HH:mm')
                                        : '—'}
                                    {original.end_time && ` - ${dayjs(original.end_time).format('HH:mm')}`}
                                </Typography>
                            </Box>
                        )}

                        {action === 'change' && requestedTime && (
                            <Box>
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                    Nový čas ({sideLabel}):
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, pl: 1, mt: 0.5 }}>
                                    {dayjs(requestedTime).format('DD.MM.YYYY HH:mm')}
                                </Typography>
                            </Box>
                        )}

                        {action === 'delete' && (
                            <Typography variant="body2" color="error.main" sx={{ pl: 1 }}>
                                {side === 'end'
                                    ? 'Odchod sa zruší, príchod zostane.'
                                    : 'Vymaže sa celá zmena vrátane odchodu.'}
                            </Typography>
                        )}
                    </Box>
                );
            }

            if (request.type.endsWith('_delete')) {
                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Žiadosť o vymazanie:</Typography>
                        {original && (
                            <Box sx={{ pl: 2, borderLeft: '3px solid #F44336', py: 1 }}>
                                {original.start_time && (
                                    <Typography variant="body2" color="text.secondary">
                                        Pôvodný čas: {dayjs(original.start_time).format('DD.MM.YYYY HH:mm')}
                                        {original.end_time && ` - ${dayjs(original.end_time).format('HH:mm')}`}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            }

            const startTime = payload?.startTime ?? payload?.start_time;
            const endTime = payload?.endTime ?? payload?.end_time;

            if (startTime || endTime) {
                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>Zmena času:</Typography>

                        {original && original.start_time && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Pôvodný čas:</Typography>
                                </Box>
                                <Box sx={{ pl: 3, mb: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {dayjs(original.start_time).format('DD.MM.YYYY HH:mm')}
                                        {original.end_time && ` - ${dayjs(original.end_time).format('HH:mm')}`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>Nový čas:</Typography>
                            </Box>
                            <Box sx={{ pl: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                    {startTime ? dayjs(startTime).format('DD.MM.YYYY HH:mm') : '—'}
                                    {endTime && ` - ${dayjs(endTime).format(startTime ? 'HH:mm' : 'DD.MM.YYYY HH:mm')}`}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            }

            // Doplnenie chýbajúceho záznamu. Bez vlastnej vetvy by tu ostalo
            // holé „Attendance Create" bez časov a zamestnávateľ by nevedel,
            // čo vlastne schvaľuje.
            if (request.type === 'attendance_create') {
                const { side, requestedTime } = payload || {};
                const sideLabel = side === 'start' ? 'Príchod' : 'Odchod';
                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Doplnenie chýbajúceho záznamu – {sideLabel}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
                            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                {sideLabel}:
                            </Typography>
                        </Box>
                        <Box sx={{ pl: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {requestedTime ? dayjs(requestedTime).format('DD.MM.YYYY HH:mm') : '—'}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            Schválením sa dochádzkový záznam vytvorí — zamestnanec sa vtedy nepípol.
                        </Typography>
                    </Box>
                );
            }

            if (request.type === 'break_create') {
                const { startTime: bStart, endTime: bEnd } = payload || {};
                return (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Doplnenie chýbajúcej prestávky
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
                            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                Prestávka:
                            </Typography>
                        </Box>
                        <Box sx={{ pl: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {bStart ? dayjs(bStart).format('DD.MM.YYYY HH:mm') : '—'}
                                {bEnd && ` - ${dayjs(bEnd).format('HH:mm')}`}
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            Schválením sa prestávka pridá k existujúcej zmene.
                        </Typography>
                    </Box>
                );
            }

            return <Typography variant="body2" color="text.secondary">Žiadne detaily v žiadosti.</Typography>;

        } catch (e) {
            console.error("Error parsing or rendering payload:", e, request.payload);
            return <Typography variant="body2" color="text.secondary">Nepodarilo sa zobraziť detaily.</Typography>;
        }
    };

    const formatRequestType = (type) => {
        if (type === 'attendance_shift_update') {
            return 'Úprava dochádzky';
        }
        if (type === 'attendance_create') {
            return 'Doplnenie dochádzky';
        }
        if (type === 'break_create') {
            return 'Doplnenie prestávky';
        }
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const handleCardClick = (requestId) => {
        setExpandedCards(prev => ({
            ...prev,
            [requestId]: !prev[requestId]
        }));
    };
    
    const filteredRequests = requests.filter(request =>
        request.employee_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto', bgcolor: 'background.default' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
                {t('pages.requests.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                {t('pages.requests.description')}
            </Typography>

            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, position: 'sticky', top: '10px', zIndex: 1200 }}>{success}</Alert>}

            <TextField
                fullWidth
                variant="outlined"
                placeholder={t('pages.requests.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        backgroundColor: 'background.paper',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)'
                    },
                    gap: 3,
                }}
            >
                {filteredRequests.length > 0 ? filteredRequests.map((request) => (
                    <Paper
                        key={request.id}
                        onClick={() => handleCardClick(request.id)}
                        sx={{
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            textDecoration: 'none',
                            color: 'inherit',
                            borderRadius: 4,
                            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
                            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                            cursor: 'pointer',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px rgba(0,0,0,0.1)',
                            },
                            bgcolor: 'background.paper',
                            height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 3, pb: 0 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 50, height: 50, fontSize: '1.2rem', fontWeight: 'bold' }}>
                                {getInitials(request.employee_name)}
                            </Avatar>
                            <Box sx={{ ml: 2, minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {request.employee_name}
                                </Typography>
                                <Chip
                                    label={formatRequestType(request.type)}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: 'rgba(37, 99, 235, 0.14)',
                                        color: 'primary.main',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ px: 3, py: 2, flexGrow: 1 }}>
                            {renderRequestDetails(request)}
                            
                            {request.reason && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                        Dôvod:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        "{request.reason}"
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Hlavná denná akcia — tlačidlá natrvalo viditeľné, žiadny accordion */}
                        <Box sx={{
                            px: 3,
                            py: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            gap: 1.5,
                        }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="medium"
                                startIcon={<HighlightOffIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(request, 'reject');
                                }}
                                sx={{ flex: 1, borderRadius: '10px' }}
                            >
                                {t('pages.requests.reject')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="medium"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(request, 'approve');
                                }}
                                sx={{ flex: 1, borderRadius: '10px', boxShadow: 'none', color: '#fff', '&:hover': { boxShadow: 'none' } }}
                            >
                                {t('pages.requests.approve')}
                            </Button>
                        </Box>
                    </Paper>
                )) : (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, gridColumn: '1 / -1' }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('pages.requests.noRequests') || 'No pending requests found.'}
                        </Typography>
                    </Paper>
                )}
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to {actionType} this request from <strong>{selectedRequest?.employee_name}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleConfirmAction} color={actionType === 'approve' ? 'success' : 'error'} autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RequestManagement; 